import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DottedText } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { numFormatterWithSuffix, numberToCurrencyFormatter } from './helper';
import { CostValues, MemberValues } from './summaryReportHelper';
import wasteCalculatorPageMessages from '../messages/wasteCalculatorPageMessages';
import { WASTE_CALCULATOR_DETAILS } from '../routes';
import { useNavigate } from 'react-router-dom';
import {
  Admission,
  AvoidableDrugs,
  Imaging,
  MedPharmaDrugs,
  NOCDrugs,
  Procedures,
  Readmissions,
  Visits,
} from '../../../public/Images/WasteCalculator';

const constantMessages = wasteCalculatorPageMessages.WASTE_CALCULATOR_PAGE;

export const getWasteCalculatorTableHeaders = () => {
  return (
    <>
      <TableCell style={{ width: '40px', padding: '0px' }} />
      <TableCell
        align="left"
        style={{
          width: '362px',
        }}
      >
        {constantMessages.TABLE_HEADERS[0]}
      </TableCell>
      <TableCell
        align="right"
        style={{
          width: '217px',
        }}
      >
        {constantMessages.TABLE_HEADERS[1]}
      </TableCell>
      <TableCell
        align="right"
        style={{
          width: '217px',
        }}
      >
        {constantMessages.TABLE_HEADERS[2]}
      </TableCell>
      <TableCell
        align="right"
        style={{
          width: '158px',
        }}
      >
        {constantMessages.TABLE_HEADERS[3]}
      </TableCell>
      <TableCell
        align="right"
        style={{
          paddingRight: '45px',
        }}
      >
        {constantMessages.TABLE_HEADERS[4]}
      </TableCell>
    </>
  );
};

export const getWasteCalculatorTableRows = (row: any, index: number) => {
  const [open, setOpen] = React.useState(true);
  const navigate = useNavigate();

  const { classes } = makeStyles()({
    tableRow: {
      boxSizing: 'border-box',
      width: '1182px',
      height: '56px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
      '& .MuiTableCell-body': {
        fontSize: '14px',
        lineHeight: '160%',
        fontWeight: 400,
        color: patientAccessTheme.palette.text.highemphasis,
      },
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
      borderBottom: 'none',
    },
    subRow: {
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
      backgroundColor: patientAccessTheme.palette.background.paper,
      '& .MuiTableCell-root': {
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 400,
        color: patientAccessTheme.palette.text.mediumemphasis,
      },
    },
  })();

  return (
    <React.Fragment key={index}>
      {/* main row */}
      <TableRow className={classes.tableRow}>
        <TableCell style={{ paddingRight: '0px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            style={{ padding: '0px' }}
          >
            {open ? (
              <KeyboardArrowUpIcon
                style={{
                  color: patientAccessTheme.palette.status.icon,
                  transform: 'rotate(180deg)',
                }}
              />
            ) : (
              <KeyboardArrowDownIcon
                style={{
                  transform: 'rotate(-90deg)',
                  color: patientAccessTheme.palette.status.icon,
                }}
              />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          align="left"
          style={{
            width: '362px',
          }}
        >
          {row?.mainCategory ?? '-'}
        </TableCell>
      </TableRow>
      {/* sub row */}
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table>
              <TableBody>
                {row?.wastageCategories?.map((subRow: any, index: number) => {
                  return (
                    <TableRow key={index} className={classes.subRow}>
                      <TableCell
                        align="left"
                        style={{
                          width: '402px',
                          paddingLeft: '56px',
                        }}
                      >
                        <div
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            navigate(WASTE_CALCULATOR_DETAILS, {
                              state: {
                                selectedFactor: subRow?.category,
                                claimIds: subRow?.claims_ids,
                              },
                            });
                          }}
                        >
                          <DottedText text={subRow?.category ?? '-'} />
                        </div>
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          width: '217px',
                        }}
                      >
                        {numberToCurrencyFormatter(
                          subRow?.total_cost ?? 0,
                          true,
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          width: '217px',
                        }}
                      >
                        {numberToCurrencyFormatter(
                          subRow?.average_cost ?? 0,
                          true,
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          width: '158px',
                        }}
                      >
                        {numberToCurrencyFormatter(
                          subRow?.members ?? 0,
                          false,
                          0,
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          paddingRight: '45px',
                        }}
                      >
                        {numberToCurrencyFormatter(
                          subRow?.claims_count ?? 0,
                          false,
                          0,
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
export interface CategoryData {
  total_cost: number;
  members: number;
  memberIds: string[];
  claims_count: number;
  average_cost: number;
  category: string;
  claims_ids: string[];
}
export const getTotalCostValue = (cost: number) => {
  const maxCost = 999999999.99;
  if (cost > maxCost) {
    return numFormatterWithSuffix(cost ?? 0);
  } else {
    return numberToCurrencyFormatter(cost ?? 0, true);
  }
};

export const formatWasteCalculatorSummary = (data: any) => {
  let totalClaims = 0;
  const memberValues: MemberValues = {
    members: '',
    employees: '',
    dependents: '',
  };
  const costValues: CostValues = {
    rx: '',
    medical: '',
    total: '',
  };

  let totalMembers = 0;
  let totalCost = 0;

  data?.total_cost?.map((item: any) => {
    const cost = numberToCurrencyFormatter(item.total_cost);
    if (item.claim_type === 'rx') {
      costValues.rx = cost;
    } else if (item.claim_type === 'medical') {
      costValues.medical = cost;
    }
    totalCost += item.total_cost;
    totalClaims += item.claims_count;
  });

  data?.total_members?.map((item: any) => {
    const members = numberToCurrencyFormatter(
      item.total_enrolles_count,
      false,
      0,
    );
    if (item.member_role === 'Employee') {
      memberValues.employees = members;
    } else if (item.member_role === 'Dependent') {
      memberValues.dependents = members;
    }
    totalMembers += item.total_enrolles_count;
  });

  costValues.total = numberToCurrencyFormatter(totalCost);
  memberValues.members = numberToCurrencyFormatter(totalMembers, false, 0);

  return {
    memberValues,
    costValues,
    totalClaims,
  };
};

export const formatWastageCategoriesData = (data: any) => {
  const localCategoryMembersMap: Map<string, string[]> = new Map<
    string,
    string[]
  >();

  const wastageCategoriesData = [
    {
      mainCategory: 'Place/ Point of Care',
      wastageCategories: [
        {
          category: 'Avoidable Hospital Admissions',
          image: Admission,
        },
        {
          category: 'Avoidable Hospital Readmissions',
          image: Readmissions,
        },
        {
          category: 'Avoidable ED Visits',
          image: Visits,
        },
      ],
    },
    {
      mainCategory: 'Healthcare Factors',
      wastageCategories: [
        {
          category: 'Avoidable procedures',
          image: Procedures,
        },
        {
          category: 'Avoidable Drugs',
          image: AvoidableDrugs,
        },
        {
          category: 'Overuse of Imaging',
          image: Imaging,
        },
      ],
    },
    {
      mainCategory: 'Abuse Factors',
      wastageCategories: [
        {
          category: 'Overuse of Not Otherwise Classified (NOC) drugs',
          image: NOCDrugs,
        },
        {
          category: 'Wastage of Medpharma drugs',
          image: MedPharmaDrugs,
        },
      ],
    },
  ];

  const categoryDataMap: { [category: string]: CategoryData } = {};

  //map all categories to categoryDataMap
  data.wastage_categories?.forEach((category: CategoryData) => {
    categoryDataMap[category.category] = category;
  });

  //check the matching category and update the wastageCategoriesData
  const updatedWastageCategoriesData = wastageCategoriesData.map(
    (mainCategory: any) => {
      const updatedWastageCategories = mainCategory.wastageCategories.map(
        (wastageCategory: CategoryData) => {
          const matchingCategory = categoryDataMap[wastageCategory.category];
          if (matchingCategory) {
            localCategoryMembersMap.set(
              matchingCategory.category,
              matchingCategory.memberIds,
            );
          }
          return matchingCategory
            ? { ...wastageCategory, ...matchingCategory }
            : wastageCategory;
        },
      );

      return { ...mainCategory, wastageCategories: updatedWastageCategories };
    },
  );

  return {
    updatedWastageCategoriesData: updatedWastageCategoriesData,
    localCategoryMembersMap: localCategoryMembersMap,
  };
};
